import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FiLinkedin, FiGithub, FiMail, FiPhone, FiSun, FiMoon, FiGrid, FiMonitor, FiFileText } from 'react-icons/fi';
import Lenis from 'lenis';

const LINKS = [
  { id: 'linkedin', label: 'LinkedIn', Icon: FiLinkedin, href: 'https://www.linkedin.com/in/yoyal-limbu/', external: true },
  { id: 'github', label: 'GitHub', Icon: FiGithub, href: 'https://github.com/y000yal', external: true },
  { id: 'mail', label: 'Email', Icon: FiMail, href: 'mailto:yoyal.limbu@gmail.com' },
  { id: 'phone', label: 'Phone', Icon: FiPhone, href: 'tel:+9779818454474' },
];

/* ───────────────────────────────────────────────────────────────────────────
   Journey stops — newest first. Scrolling down rewinds time.
   ─────────────────────────────────────────────────────────────────────────── */
// Portrait per stop. The files are numbered oldest to newest; yoel.webp is the
// current one. Any stop without a photo falls back to PHOTO, and a missing file
// falls back again to the company initials on a flat ground.
export const PHOTO = '/journey/yoel.webp';

export const STOPS = [
  {
    id: 'themegrill',
    company: 'ThemeGrill',
    role: 'Senior PHP Developer',
    end: null,   // still here: the label and the odometer follow the clock
    year: 2024,
    place: 'Kathmandu, Nepal',
    note: 'Building and maintaining WordPress plugins used at scale: User Registration & Membership, PDFDraft and others. Backwards compatibility stops being a preference and becomes a promise.',
    photo: '/journey/yoel.webp',
    quip: 'Other people’s sites run this code. No pressure.',
    tech: ['PHP', 'WordPress', 'OOP', 'jQuery'],
    accent: '#f0592b',           // ThemeGrill flame orange
    bg: ['#080b12', '#1a2438'],  // its navy wordmark, as the card ground
    badge: 'EMP-2024-0417',
  },
  {
    id: 'hitachi',
    company: 'Hitachi Energy',
    role: 'Software Engineer',
    end: 2024,
    year: 2023,
    place: 'Kathmandu, Nepal',
    note: 'TrmTracker, an energy trade and risk management platform. Legacy code, real money, and a security review that taught me to read before I write.',
    photo: '/journey/4.webp',
    quip: 'Legacy code, live markets. Read twice, ship once.',
    tech: ['PHP', 'Azure', 'TSQL', 'JavaScript'],
    accent: '#e60027',           // Hitachi red
    bg: ['#0d0507', '#2b0a11'],
    badge: 'HE-2023-1182',
  },
  {
    id: 'genius',
    company: 'Genius Systems',
    role: 'Software Engineer',
    end: 2023,
    year: 2021,
    place: 'Lalitpur, Nepal',
    note: 'Three systems back to back: camera management, a fintech proxy, and WiFi hotspot control. The Laravel and Docker years, and where I learned to ship.',
    photo: '/journey/3.webp',
    quip: 'Three products, two years, one very warm laptop.',
    tech: ['Laravel', 'Docker', 'MySQL', 'REST'],
    accent: '#22a45c',           // Genius Systems green
    bg: ['#050a07', '#0d2417'],
    badge: 'GS-2021-0043',
  },
  {
    id: 'codecater',
    company: 'CodeCater Studio',
    role: 'Web Developer',
    end: 2021,
    year: 2019,
    place: 'Kathmandu, Nepal',
    note: 'SmartBus, a bus ticket booking system. The first code I wrote that strangers actually used, which changes how you think about edge cases.',
    photo: '/journey/2.webp',
    quip: 'Shipped bus tickets before I could drive.',
    tech: ['Laravel', 'Yii2', 'Vue', 'Bootstrap'],
    accent: '#2e8bc9',           // Code Cater blue
    bg: ['#04090f', '#0b2233'],
    badge: 'CC-2019-0007',
  },
  {
    id: 'college',
    company: 'College',
    role: 'Certified Engineer',
    wink: true,
    end: 2019,
    year: 2015,
    place: 'Bachelor of Computer Science',
    note: 'Computer science, four years of it. Mostly curiosity, cheap tea, and the slow realisation that I would rather build things than read about them.',
    idLabel: 'STUDENT ID',   // a college badge is not staff
    photo: '/journey/1.webp',
    quip: 'Four years, one degree, a browser full of tabs.',
    tech: ['C', 'Java', 'DBMS', 'Algorithms'],
    accent: '#5b6ee1',
    bg: ['#08080f', '#171a33'],
    badge: 'STU-2015-2291',
  },
];

/* ───────────────────────────────────────────────────────────────────────────
   Rope physics — verlet chain. Pure functions so they stay testable.
   ─────────────────────────────────────────────────────────────────────────── */
export const SEG = 20;
export const SEGMENTS = 14;

export function createChain(anchorX, anchorY, segments = SEGMENTS, seg = SEG) {
  const pts = [];
  for (let i = 0; i <= segments; i++) {
    pts.push({ x: anchorX, y: anchorY + i * seg, px: anchorX, py: anchorY + i * seg });
  }
  return pts;
}

// One physics tick: integrate, then relax segment lengths.
// `held` pins the last point (drag), `anchor` always pins the first.
export function stepChain(pts, { anchor, held = null, gravity = 0.62, drag = 0.952, seg = SEG, iterations = 12, maxV = 24 }) {
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i];
    let vx = (p.x - p.px) * drag;
    let vy = (p.y - p.py) * drag;
    // Ceiling on per-point speed. Without it a fast drag pumps energy into the
    // chain and the release swing has nothing to do with how hard you threw it.
    const sp = Math.hypot(vx, vy);
    if (sp > maxV) { vx = (vx / sp) * maxV; vy = (vy / sp) * maxV; }
    p.px = p.x;
    p.py = p.y;
    p.x += vx;
    p.y += vy + gravity;
  }

  const last = pts.length - 1;
  for (let k = 0; k < iterations; k++) {
    pts[0].x = anchor.x;
    pts[0].y = anchor.y;
    if (held) {
      pts[last].x = held.x;
      pts[last].y = held.y;
    }
    for (let i = 0; i < last; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d = Math.hypot(dx, dy) || 0.0001;
      // Elastic, and one-directional: a rope pulls back when stretched and does
      // nothing when slack. Correcting compression shoves slack coils apart,
      // which is what shook the whole chain when the card was lifted.
      if (d <= seg) continue;
      // Lower factor + fewer iterations = a softer, stretchier strap.
      const diff = ((d - seg) / d) * 0.2;
      const ox = dx * diff;
      const oy = dy * diff;
      const aFixed = i === 0;
      const bFixed = held && i + 1 === last;
      if (aFixed && bFixed) continue;
      if (aFixed) { b.x -= ox * 2; b.y -= oy * 2; }
      else if (bFixed) { a.x += ox * 2; a.y += oy * 2; }
      else { a.x += ox; a.y += oy; b.x -= ox; b.y -= oy; }
    }
  }
  pts[0].x = anchor.x;
  pts[0].y = anchor.y;
  if (held) { pts[last].x = held.x; pts[last].y = held.y; }
  return pts;
}

export const NOW_YEAR = new Date().getFullYear();

// A stop with end === null is the current one: it runs to today, so both its
// label and its year read off the clock instead of a baked-in string.
export function spanLabel(stop) {
  return stop.end === null ? `${stop.year} to Present` : `${stop.year} to ${stop.end}`;
}
export function endYear(stop, now = NOW_YEAR) {
  return stop.end === null ? now : stop.end;
}

const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Mix a #rrggbb toward white. f = 1 is pure white.
export function tintHex(hex, f) {
  const ch = hex.slice(1).match(/[0-9a-f]{2}/gi).map(h => parseInt(h, 16));
  return `#${ch.map(v => Math.round(v + (255 - v) * f).toString(16).padStart(2, '0')).join('')}`;
}

const smoother = (f) => f * f * f * (f * (f * 6 - 15) + 10);

// Which way a tap turns the card. lx is the grab offset, derived from clientX,
// so it is already the side of the SCREEN that was tapped.
//
// I previously inverted this when the back was showing, reasoning that the
// card local +X points screen-left at 180 so the sign must flip to keep the
// tapped edge moving away. In practice that made the same side alternate
// direction on every other tap, which is worse than any theoretical
// inconsistency: tap left, it turns left, every time.
export function flipDirection(lx) {
  return (lx ?? 0) >= 0 ? 1 : -1;
}

// Which detent the crown is on. One per knurl tooth rather than per stop, so a
// turn ticks continuously instead of clicking four times across the whole range.
export function detentIndex(p, subs = DIAL_SUBS) {
  return Math.round(p * (subs + 1));
}

// Device tilt -> lateral anchor offset in px. gamma is the left/right roll in
// degrees; beyond about 35 degrees the phone is not really being held at the
// screen any more, so it clamps rather than pinning the card sideways.
export function tiltSway(gamma, gain = 0.62, cap = 22) {
  if (typeof gamma !== 'number' || Number.isNaN(gamma)) return 0;
  return clamp(gamma * gain, -cap, cap);
}

// One number that fits the strap-and-card assembly into the viewport. Height is
// the binding constraint: anchor drop + rope + card is roughly 640px.
export function stageScale(w = window.innerWidth, h = window.innerHeight) {
  return clamp(Math.min(w / 470, h / 760), 0.55, 1);
}

// Screen space -> physics space. The stage is scaled about its top centre, so
// undo that before handing coordinates to the solver: without it the card lags
// the finger by a factor of the scale on every small screen.
export function toStage(clientX, clientY, rect, k) {
  return [
    (clientX - rect.left - rect.width / 2) / k + rect.width / 2,
    (clientY - rect.top) / k,
  ];
}

// The dial is an arc, not a full circle: a full circle has no unambiguous start
// and the two ends would meet. 240 degrees centred on 12 o'clock reads as a
// scrubber and still leaves room for a label inside.
// Hinged on the RIGHT edge, so the visible half runs from straight up (360)
// anticlockwise through 9 o'clock (270) to straight down (180).
export const DIAL_SWEEP = 180;
export const DIAL_TOP = 360;
export const DIAL_CENTER = 270;  // where the active stop is always parked
export const DIAL_STEP = 27;     // degrees between stops on the ring
export const DIAL_SUBS = 3;      // minor ticks between majors, for the knurl


// Screen angle of stop `i` when the ring is turned to `progress`. At i ===
// progress this is exactly DIAL_CENTER, which is the whole point.
export function crownAngle(i, progress, step = DIAL_STEP, center = DIAL_CENTER) {
  return center - (i - progress) * step;
}

// Pointer offset from the ring centre, as a screen angle in [0, 360).
export function pointerAngle(dx, dy) {
  return (((Math.atan2(dx, -dy) * 180) / Math.PI) + 360) % 360;
}

// Turning the crown is relative: how far it has rotated since the grab, in
// stops. Absolute mapping would make the ring jump to wherever you touched.
export function crownProgress(startProgress, startDeg, nowDeg, count, step = DIAL_STEP) {
  let d = nowDeg - startDeg;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  // Direction follows the wheel, not the timeline: dragging the near edge of the
  // crown downward should bring older stops up into the marker.
  return clamp(startProgress + d / step, 0, count - 1);
}

// Pointer offset from the dial centre -> progress along the arc. Angle is
// measured from straight up, clockwise, so it matches how the knob is drawn.
// Point on the arc, for drawing.
export function dialPoint(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

// Smootherstep each whole step: long calm hold on a stop, clean sweep between.
export function easeProgress(p) {
  const b = Math.floor(p);
  return b + smoother(p - b);
}

// Degrees of Y rotation for a scroll position. Edge-on (90 and 270) is where a
// card is geometrically a line, and scroll can STOP anywhere — so the curve
// plateaus at 0/180/360, where a face is square to the camera, and sprints
// through the perpendicular angles in between. Two smootherstep halves do it.
export function flipDegrees(p) {
  const b = Math.floor(p);
  const f = p - b;
  // smootherstep twice: flatter plateaus, steeper transit. One pass leaves ~15%
  // of the scroll range parked near an edge, two passes cut that to ~8%.
  const S = (x) => smoother(smoother(x));
  const half = f < 0.5 ? 0.5 * S(f * 2) : 0.5 + 0.5 * S(f * 2 - 1);
  return (b + half) * 360;
}

// Smooth path through the chain. The strap stays flat — only the card turns.
export function ropePath(pts) {
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i].x + pts[i + 1].x) / 2;
    const my = (pts[i].y + pts[i + 1].y) / 2;
    d += ` Q ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)}`;
  }
  const l = pts[pts.length - 1];
  d += ` L ${l.x.toFixed(1)} ${l.y.toFixed(1)}`;
  return d;
}

const CARD_W = 250;
const CARD_H = 358;
const CARD_T = 9;      // card thickness in px, split either side of centre
const CARD_R = 18;     // corner radius, shared by the faces and the walls

// Lit from the top: bright hairline on the top edge, dark on the bottom, plus a
// full inner ring. Reads as a moulded plastic bevel rather than a flat border.
// Only the drop shadow can live on the face itself: an inset box-shadow paints
// BELOW the element's children, and the face is full of stacked photo layers,
// so the bevel has to be its own overlay on top. See <Rim />.
const DROP = '0 24px 46px rgba(0,0,0,0.5)';
const RIM = [
  'inset 0 0 0 1px rgba(255,255,255,0.26)',
  'inset 0 2px 1px -1px rgba(255,255,255,0.5)',
  'inset 0 -2px 1px -1px rgba(0,0,0,0.75)',
  'inset 2px 0 2px -2px rgba(255,255,255,0.2)',
  'inset -2px 0 2px -2px rgba(0,0,0,0.5)',
].join(', ');
const PANEL_H = 112;   // info panel height; the portrait sits clear above it
const SLOT_INSET = 20; // rope tip sits this far *inside* the card's top edge
const MAX_HANG = 1.05; // radians, ~60deg. A badge on a strap cannot exceed this.
const DIAL_R = 94;     // crown radius. Big wheel, small window onto it.
const DIAL_VIS = 62;   // how much of the wheel actually shows past the edge
const DIAL_BOX = DIAL_R * 2 + 26;

// Half turns a throw is worth. Below the threshold the card just swings — a
// gentle drag should not flip the card.
export function flipHalves(vx, minThrow = 6) {
  if (Math.abs(vx) < minThrow) return 0;
  return Math.sign(vx) * clamp(Math.round(Math.abs(vx) / 8), 1, 3);
}

// Is the badge hanging still? A tap only flips from rest; tapping a card that is
// already swinging should just interrupt the swing.
export function isAtRest(pts, angleVel = 0, spinVel = 0, tol = 1.6) {
  if (Math.abs(angleVel) > 0.02 || Math.abs(spinVel) > 0.4) return false;
  for (let i = 1; i < pts.length; i++) {
    if (Math.hypot(pts[i].x - pts[i].px, pts[i].y - pts[i].py) > tol) return false;
  }
  return true;
}

const Wink = ({ size = 17 }) => (
  <svg
    width={size} height={size} viewBox="0 0 32 32"
    style={{ display: 'inline-block', verticalAlign: '-3px', flex: 'none' }}
    aria-hidden="true"
  >
    {/* body, then a darker crescent along the bottom right for volume */}
    <circle cx="16" cy="16" r="15" fill="#f7b32b" />
    <path d="M28.5 10.5A15 15 0 0 1 6 27.5A15 15 0 0 0 28.5 10.5Z" fill="#dc9312" />
    <circle cx="16" cy="16" r="15" fill="none" stroke="#c47f0c" strokeWidth="1" />
    {/* brows */}
    <path d="M7.5 11.5q3-2.6 6 0" fill="none" stroke="#5a3410" strokeWidth="2" strokeLinecap="round" />
    <path d="M19 10.6q3-2.4 6 0" fill="none" stroke="#5a3410" strokeWidth="2" strokeLinecap="round" />
    {/* left eye winking shut, right eye open */}
    <path d="M7.8 16.4q2.7 2.2 5.4 0" fill="none" stroke="#3f2409" strokeWidth="2.2" strokeLinecap="round" />
    <ellipse cx="21.8" cy="16" rx="3.1" ry="3.6" fill="#fff" />
    <ellipse cx="21.8" cy="16.3" rx="2.1" ry="2.6" fill="#241505" />
    {/* open smile with a tooth line */}
    <path d="M9 20.6h14a7 7 0 0 1-14 0Z" fill="#3f2409" />
    <path d="M10.6 20.6h10.8a5.4 5.4 0 0 1-.7 1.7H11.3a5.4 5.4 0 0 1-.7-1.7Z" fill="#fff" />
  </svg>
);

/* ───────────────────────────────────────────────────────────────────────────
   Card faces
   ─────────────────────────────────────────────────────────────────────────── */
// Top layer on both faces, so the bevel sits over the artwork, not under it.
const Rim = () => (
  <div style={{
    position: 'absolute', inset: 0, borderRadius: CARD_R,
    boxShadow: RIM, pointerEvents: 'none', zIndex: 9,
  }} />
);

// One layer per stop, crossfaded. `eager` only for the first: the rest are in
// the viewport at zero opacity, so marking them lazy keeps them off the critical
// path without stopping them loading before you scroll to them.
const Portrait = ({ stop, first = false }) => {
  const [failed, setFailed] = useState(false);
  const initials = stop.company.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      position: 'absolute', inset: 0, background: stop.bg[1],
      display: 'grid', placeItems: 'center', overflow: 'hidden',
    }}>
      {!failed ? (
        <img
          src={stop.photo || PHOTO}
          alt={`Yoyal Limbu at ${stop.company}`}
          draggable={false}
          onError={() => setFailed(true)}
          width={CARD_W}
          height={CARD_H}
          fetchpriority={first ? 'high' : 'low'}
          loading={first ? 'eager' : 'lazy'}
          decoding="async"
          style={{
            // Fills the card, cropped tight on the face. The shirt crest sits
            // behind the info panel and that is fine — it is not the subject.
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center 18%',
          }}
        />
      ) : (
        <div style={{
          fontFamily: '"Segoe UI", Inter, sans-serif', fontSize: 74, fontWeight: 800,
          color: 'rgba(255,255,255,0.22)', letterSpacing: '-3px', userSelect: 'none',
        }}>{initials}</div>
      )}
    </div>
  );
};

const CardFront = ({ stops, weights, textWeights, hint }) => (
  <div style={{
    position: 'absolute', inset: 0, borderRadius: CARD_R, overflow: 'hidden', background: '#05060a',
    boxShadow: DROP,
  }}>
    {/* first-load affordance: this thing is grabbable */}
    <div style={{
      position: 'absolute', top: 13, right: 13, zIndex: 4,
      display: 'flex', alignItems: 'center', gap: 7,
      opacity: hint ? 1 : 0, transition: 'opacity .45s', pointerEvents: 'none',
      fontFamily: '"Segoe UI", Inter, sans-serif',
    }}>
      <span className="lnd-dot" />
    </div>

    {/* punched slot the lanyard threads through */}
    <div style={{
      position: 'absolute', top: SLOT_INSET - 4, left: '50%', width: 46, height: 8, marginLeft: -23,
      borderRadius: 4, background: '#05060a', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.9)', zIndex: 2,
    }} />
    {stops.map((s, i) => (
      <div key={s.id} style={{ position: 'absolute', inset: 0, opacity: weights[i] }}>
        <Portrait stop={s} first={i === 0} />
      </div>
    ))}

    {/* frosted info panel */}
    <div style={{
      position: 'absolute', left: 0, right: 0, bottom: 0, height: PANEL_H,
      background: 'rgba(6,7,12,0.9)',
      borderTop: '1px solid rgba(255,255,255,0.1)',
      padding: '0 16px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      fontFamily: '"Segoe UI", Inter, sans-serif', color: '#fff',
    }}>
      <div style={{ fontSize: 21, fontWeight: 600, lineHeight: 1.12, letterSpacing: '-0.4px' }}>Yoyal</div>
      <div style={{ fontSize: 21, fontWeight: 600, lineHeight: 1.12, letterSpacing: '-0.4px', marginBottom: 7 }}>Limbu</div>
      <div style={{ position: 'relative', height: 16, marginBottom: 8 }}>
        {stops.map((s, i) => (
          <div key={s.id} style={{
            position: 'absolute', left: 0, top: 0, whiteSpace: 'nowrap',
            fontSize: 12, fontWeight: 500, color: s.accent, opacity: textWeights[i],
            display: 'flex', alignItems: 'center', gap: 5,
          }}>{s.role}{s.wink ? <Wink size={14} /> : null}</div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', position: 'relative', height: 14 }}>
        {stops.map((s, i) => (
          <React.Fragment key={s.id}>
            <div style={{ position: 'absolute', left: 0, fontSize: 10, letterSpacing: '1.2px', color: 'rgba(255,255,255,0.7)', opacity: textWeights[i], whiteSpace: 'nowrap' }}>{s.idLabel || 'STAFF ID'}</div>
            <div style={{ position: 'absolute', right: 0, fontSize: 10.5, color: 'rgba(255,255,255,0.78)', opacity: textWeights[i], whiteSpace: 'nowrap' }}>{s.badge}</div>
          </React.Fragment>
        ))}
      </div>
    </div>

    <Rim />
  </div>
);

const CardBack = ({ stops, textWeights }) => (
  <div style={{
    position: 'absolute', inset: 0, borderRadius: CARD_R, overflow: 'hidden',
    background: '#101018',
    boxShadow: DROP,
    fontFamily: '"Segoe UI", Inter, sans-serif', color: '#fff',
    padding: '18px 16px', display: 'flex', flexDirection: 'column',
  }}>
    <div style={{ fontSize: 9.5, letterSpacing: '2px', color: 'rgba(255,255,255,0.62)', textTransform: 'uppercase' }}>
      Access card
    </div>
    <div style={{ height: 34, marginTop: 12, background: '#1b1b25', borderRadius: 3 }} />
    <div style={{ position: 'relative', flex: 1, marginTop: 14, overflow: 'hidden' }}>
      {stops.map((s, i) => (
        <div key={s.id} style={{ position: 'absolute', inset: 0, opacity: textWeights[i] }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{spanLabel(s)}</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{s.company}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{s.place}</div>
          <div style={{
            fontSize: 12.5, lineHeight: 1.45, color: 'rgba(255,255,255,0.9)', marginTop: 14,
            fontStyle: 'italic', letterSpacing: '0.1px',
            display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3,
            overflow: 'hidden',
          }}>{s.quip}</div>
        </div>
      ))}
    </div>

    {/* contact rail. stopPropagation on pointerdown keeps the card's drag
        handler from swallowing the click, so the link navigates normally. */}
    <div
      onPointerDown={e => e.stopPropagation()}
      style={{ display: 'flex', gap: 8, marginBottom: 14 }}
    >
      {LINKS.map(({ id, label, Icon, href, external }) => (
        <a
          key={id}
          href={href}
          aria-label={label}
          title={label}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className="lnd-link"
        >
          <Icon size={14} />
        </a>
      ))}
    </div>

    <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.58)', letterSpacing: '1px' }}>
      PROPERTY OF THE BEARER · RETURN IF FOUND
    </div>

    <Rim />
  </div>
);

/* ───────────────────────────────────────────────────────────────────────────
   Landing
   ─────────────────────────────────────────────────────────────────────────── */
export default function Landing({ onEnterDesktop }) {
  const sceneRef = useRef(null);
  const pathRef = useRef(null);
  const cardRef = useRef(null);
  const shadowRef = useRef(null);
  const lenisRef = useRef(null);
  const limitRef = useRef(0);
  const dialRef = useRef(null);
  const dragDial = useRef(null);   // { deg, from } while turning
  const progressRef = useRef(0);   // latest progress, for grab-relative turning

  // physics state kept out of React so the rAF loop never re-renders
  const sim = useRef({
    pts: null,
    anchor: { x: 0, y: 0 },
    target: null,      // where the pointer wants the attach point
    heldVx: 0,         // spring pull per frame — the real throw speed
    grab: null,        // { lx, ly } grab point in card-local space
    angle: 0,
    angleVel: 0,
    spinScroll: 0,     // scroll-driven Y flip, one full turn per section
    spinY: 0,
    spinVel: 0,
    spinTarget: 0,   // absolute degrees. A flip sets this, never a raw impulse.
    releaseDamp: 0,  // 1 at release, decays — extra drag right after letting go
    wasAtRest: true, // was it hanging still when grabbed? decides tap-to-flip
    anchorBase: 0,   // where the anchor sits with no sway applied
    sway: 0,         // current lateral anchor offset, px
    swayWant: 0,     // what scroll and rotation are asking for
    spinPrev: 0,     // last frame's scroll flip, to get its rate
    scale: 1,        // current stage scale, for snapping to the device pixel grid
    tilt: 0,         // device roll, degrees. Sustained, unlike scroll impulse.
    dragging: false,
    moved: false,
  });

  // scroll-derived state (drives the crossfades, so it lives in React)
  const [progress, setProgress] = useState(0); // 0 .. STOPS.length - 1
  const [hint, setHint] = useState(true);
  // same storage key as Desktop, so the two modes agree on the theme
  const [theme, setTheme] = useState(() => localStorage.getItem('portfolio-theme') || 'dark');
  const toggleTheme = useCallback(() => {
    setTheme(t => {
      const next = t === 'dark' ? 'light' : 'dark';
      localStorage.setItem('portfolio-theme', next);
      return next;
    });
  }, []);
  const light = theme === 'light';
  const [stage, setStage] = useState(() => stageScale());
  sim.current.scale = stage;
  // below this the left copy has nowhere to live except under the card
  const [narrow, setNarrow] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    // plain resize, not matchMedia: a media-query listener misses viewport
    // changes that do not come from the user (devtools, embedded frames)
    const on = () => {
      setNarrow(window.innerWidth < 1024);
      setStage(stageScale());
    };
    on();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  // scene chrome colour. The card keeps its own dark palette either way.
  const fg = (o = 1) => (light ? `rgba(17,18,24,${o})` : `rgba(255,255,255,${o})`);

  // Everything downstream reads `ease`, never raw scroll.
  const ease = easeProgress(progress);

  const idx = Math.round(ease);
  const stop = STOPS[clamp(idx, 0, STOPS.length - 1)];

  // The card turns a full 360 per section, so it is edge-on at 90 and again at
  // 270. Swapping content on the FIRST 180 means you watch the change happen
  // face-on. Hold the old face through the first half turn and swap late, in
  // the shadow of the second edge-on pass, so the card comes back around
  // already showing the next stop.
  const secLo = clamp(Math.floor(ease), 0, STOPS.length - 1);
  const secHi = clamp(secLo + 1, 0, STOPS.length - 1);
  const frac = ease - Math.floor(ease);
  const swap = clamp((frac - 0.66) / 0.2, 0, 1); // tight around the 270 transit

  // photos: only the two neighbours ever mix, and only inside that window
  const weights = STOPS.map((_, i) => {
    if (i === secLo && i === secHi) return 1;
    if (i === secLo) return 1 - swap;
    if (i === secHi) return swap;
    return 0;
  });
  // text: dead band in the middle so nothing ever double-prints
  const textWeights = STOPS.map((_, i) => {
    if (i === secLo && i === secHi) return 1;
    if (i === secLo) return clamp(1 - swap * 2.2, 0, 1);
    if (i === secHi) return clamp((swap - 0.55) * 2.2, 0, 1);
    return 0;
  });

  /* ── crown click ────────────────────────────────────────────────────────── */
  const audioRef = useRef(null);
  const noiseRef = useRef(null);
  const detentRef = useRef(null);
  const lastClickRef = useRef(0);

  // Built from an oscillator rather than a file: a detent click is a few
  // milliseconds of noise, and a request for that is more expensive than
  // generating it. Created on a gesture because of autoplay policy.
  const click = useCallback(() => {
    const ctx = audioRef.current;
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    if (now - lastClickRef.current < 0.014) return;   // no machine-gunning
    lastClickRef.current = now;

    // A tone with a pitch ramp chirps. A detent click is a noise impulse: broad
    // spectrum, no pitch, over in a few milliseconds. The decay lives in the
    // buffer so the envelope stays trivial, and the buffer is built once.
    if (!noiseRef.current) {
      const len = Math.floor(ctx.sampleRate * 0.012);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) {
        const k = 1 - i / len;
        d[i] = (Math.random() * 2 - 1) * k * k * k * k;   // sharp decay
      }
      noiseRef.current = buf;
    }

    const src = ctx.createBufferSource();
    const bp = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    src.buffer = noiseRef.current;
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(3600, now);
    bp.Q.setValueAtTime(1.1, now);
    gain.gain.setValueAtTime(0.22, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.018);
    src.connect(bp).connect(gain).connect(ctx.destination);
    src.start(now);
  }, []);

  const armAudio = useCallback(() => {
    if (audioRef.current) { audioRef.current.resume?.(); return; }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    try {
      audioRef.current = new AC();
      audioRef.current.resume?.();
    } catch { /* no audio, no clicks, no problem */ }
  }, []);

  /* ── device tilt ────────────────────────────────────────────────────────── */
  // iOS gates the sensor behind a permission call that only works inside a user
  // gesture, so it is asked for on first touch rather than on load.
  const askTilt = useCallback(() => {
    const D = window.DeviceOrientationEvent;
    if (D && typeof D.requestPermission === 'function') {
      D.requestPermission().catch(() => {});
    }
  }, []);

  // Only on touch layouts: a desktop with an accelerometer tilting the card
  // while the user sits still would be noise, not feedback.
  useEffect(() => {
    if (!narrow) return undefined;
    const on = (e) => { sim.current.tilt = e.gamma ?? 0; };
    window.addEventListener('deviceorientation', on);
    return () => {
      window.removeEventListener('deviceorientation', on);
      sim.current.tilt = 0;
    };
  }, [narrow]);

  /* ── smooth scroll ──────────────────────────────────────────────────────── */
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.3,
      smoothWheel: true,
      wheelMultiplier: 0.85,
      touchMultiplier: 1.5,
    });
    lenisRef.current = lenis;
    // Lenis only reports the limit on its scroll event. Seed it now, or the
    // crown is inert until something else scrolls the page first.
    limitRef.current = lenis.limit || (document.documentElement.scrollHeight - window.innerHeight);
    let raf = requestAnimationFrame(function loop(t) {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    });

    const onScroll = ({ scroll, limit, velocity }) => {
      limitRef.current = limit;
      const p = limit > 0 ? (scroll / limit) * (STOPS.length - 1) : 0;
      progressRef.current = p;
      setProgress(p);
      // its own curve: rests only on a face, never edge-on
      sim.current.spinScroll = flipDegrees(p);
      // scrolling asks for a little sway; the anchor, not the chain, carries it
      sim.current.swayWant = clamp(velocity * 0.5, -13, 13);
      if (scroll > 40) setHint(false);
    };
    lenis.on('scroll', onScroll);

    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, []);

  /* ── physics loop ───────────────────────────────────────────────────────── */
  useEffect(() => {
    const s = sim.current;
    const setAnchor = () => {
      s.anchorBase = window.innerWidth / 2;
      s.anchor.x = s.anchorBase + s.sway;
      s.anchor.y = -Math.min(120, window.innerHeight * 0.16);
    };
    setAnchor();
    s.pts = createChain(s.anchor.x, s.anchor.y);
    // Start stacked above the anchor so gravity drops the whole card into frame,
    // overshoots on the elastic rope, then settles. Slight x offset = it swings.
    s.pts.forEach((pt, i) => {
      const y = s.anchor.y - i * 3;
      pt.y = y; pt.py = y + 1.5;
      pt.x = s.anchor.x + (i ? 7 : 0);
      pt.px = pt.x - 1.4;
    });
    s.angleVel = 0.05;
    s.spinY = -215;
    s.spinTarget = 0;

    let raf = 0;
    const frame = () => {
      const pts = s.pts;
      if (!pts) return;

      // Sway. Two sources: how fast you are scrolling, and how fast the card is
      // turning. Both nudge the ANCHOR sideways and let the strap swing as a
      // pendulum — injecting velocity straight into the chain is what made this
      // violent before. Low-pass filtered, so it can never jerk.
      const spinRate = s.spinScroll - s.spinPrev;
      s.spinPrev = s.spinScroll;
      const want = clamp(
        s.swayWant + clamp(spinRate * 0.5, -9, 9) + tiltSway(s.tilt),
        -28, 28,
      );
      s.sway += (want - s.sway) * 0.07;
      s.swayWant *= 0.9;   // the scroll nudge fades, the tilt lean holds
      if (!s.dragging) s.anchor.x = s.anchorBase + s.sway;

      // Drag is a damped SPRING, not a pin. Pinning the end let the solver
      // over-stretch the chain, and release fired all that stored tension off
      // in one direction. A spring can only store what the pull is worth, so
      // the rope still stretches (elastic) but never loads up like a catapult.
      if (s.dragging && s.target) {
        const tip = pts[pts.length - 1];
        const gx = s.grab.lx * Math.cos(s.angle) - s.grab.ly * Math.sin(s.angle);
        const gy = s.grab.lx * Math.sin(s.angle) + s.grab.ly * Math.cos(s.angle);
        const ex = s.target.x - (tip.x + gx);
        const ey = s.target.y - (tip.y + gy);

        const pull = 0.19;
        tip.x += ex * pull;
        tip.y += ey * pull;
        // bleed the tip's own velocity while held, so no whip can accumulate
        tip.px += (tip.x - tip.px) * 0.34;
        tip.py += (tip.y - tip.py) * 0.34;
        s.heldVx = ex * pull;

        // torque once per frame, from where the grabbed corner is vs the cursor
        const cross = (gx * ey - gy * ex) / (gx * gx + gy * gy + 900);
        s.angleVel += clamp(cross * 0.45, -0.016, 0.016);
      }

      // Extra drag right after release, easing back to normal. Front-loading the
      // damping kills the initial lunge; letting it fade means the last stretch
      // glides out instead of stopping short.
      stepChain(pts, { anchor: s.anchor, drag: 0.966 - 0.032 * s.releaseDamp });
      s.releaseDamp *= 0.955;

      const end = pts[pts.length - 1];
      const prev = pts[pts.length - 2];

      // Angle target comes off the last three points, not the final segment
      // alone — one segment jitters and the card chases the jitter.
      const ref = pts[pts.length - 3] || prev;
      const ax = end.x - ref.x;
      const ay = end.y - ref.y;
      // Lift the card and this vector inverts, which sent atan2 through a full
      // turn. The previous fix snapped to +/-MAX_HANG by the sign of ax, but ax
      // hovers around zero on a straight-up drag, so the target flipped between
      // the two limits every frame: the violent shake. Instead fade the whole
      // target toward upright as the strap goes slack, which is continuous.
      const taut = clamp(ay / 45, 0, 1);
      let target = -Math.atan2(ax, Math.max(ay, 1));
      target = clamp(target, -MAX_HANG, MAX_HANG) * taut;
      let diff = target - s.angle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      // Critically damped: damping = 2*sqrt(k), so it arrives without overshoot
      // AND without the hard cutoff that a big constant decay produces. Soft k
      // gives a long, smooth tail instead of snapping into place.
      if (s.dragging) {
        s.angleVel += diff * 0.2;
        s.angleVel *= 0.74;
      } else {
        const k = 0.014;
        s.angleVel += diff * k - s.angleVel * 2 * Math.sqrt(k);
      }
      s.angle += s.angleVel;
      // hard stop at the hang limit, and kill the velocity so it can't pump
      if (s.angle > MAX_HANG) { s.angle = MAX_HANG; s.angleVel = 0; }
      if (s.angle < -MAX_HANG) { s.angle = -MAX_HANG; s.angleVel = 0; }

      // Same critically damped drive, toward an absolute target. A flip always
      // completes because the target is a definite angle, not an impulse that
      // may or may not carry past 90 degrees.
      const ks = 0.012;
      s.spinVel += (s.spinTarget - s.spinY) * ks - s.spinVel * 2 * Math.sqrt(ks);
      s.spinY += s.spinVel;

      // Z is the strap's hang angle only. CSS rotate() is clockwise in a y-down
      // frame, so the slot (card-local (0, -reach)) lands at
      // center + (reach*sin, -reach*cos). Invert that to get the center.
      const reach = CARD_H / 2 - SLOT_INSET;
      const cx = end.x - Math.sin(s.angle) * reach;
      const cy = end.y + Math.cos(s.angle) * reach;

      if (cardRef.current) {
        // a little X lean from vertical motion — reads as weight, not wobble
        let leanX = clamp((end.y - end.py) * 0.5, -7, 7);
        // Y carries both the user's flips and the slow scroll flip
        let flipY = s.spinY + s.spinScroll;

        // Type is fuzzy because a 3D-transformed layer is rasterised once then
        // resampled: a residual 0.3deg rotation or a half-pixel offset blurs
        // every glyph, and a critically damped spring never reaches exactly
        // zero. So once motion drops below the visible threshold, snap the
        // values and emit a plain integer translate — no rotation, no
        // perspective. That path renders text natively sharp.
        if (!s.dragging
            && Math.abs(s.spinVel) < 0.02 && Math.abs(s.spinTarget - s.spinY) < 0.1
            && Math.abs(s.angleVel) < 0.001 && Math.abs(s.angle) < 0.015
            && Math.abs(end.x - end.px) < 0.08 && Math.abs(end.y - end.py) < 0.08) {
          s.spinY = s.spinTarget;
          s.spinVel = 0;
          s.angle = 0;
          s.angleVel = 0;
          leanX = 0;
          flipY = s.spinY + s.spinScroll;
        }

        const turn = (((flipY % 360) + 360) % 360);       // 0..359
        const facing = turn === 0 || turn === 180;         // square to the camera
        const flat = s.angle === 0 && leanX === 0 && facing;
        // snap so that scale * position is a whole device pixel, not a whole
        // physics pixel: at 0.877 the two are not the same thing
        const snap = (v) => Math.round(v * s.scale) / s.scale;
        cardRef.current.style.transform = flat
          ? `translate3d(${snap(cx - CARD_W / 2)}px, ${snap(cy - CARD_H / 2)}px, 0)` +
            (turn === 180 ? ' rotateY(180deg)' : '')
          : `perspective(1700px) translate3d(${(cx - CARD_W / 2).toFixed(1)}px, ${(cy - CARD_H / 2).toFixed(1)}px, 0)` +
            ` rotate(${((s.angle * 180) / Math.PI).toFixed(2)}deg)` +
            ` rotateY(${flipY.toFixed(2)}deg) rotateX(${leanX.toFixed(2)}deg)`;
      }
      if (shadowRef.current) {
        // face = 1 when the card faces us, 0 edge-on: the shadow thins with it
        const face = Math.abs(Math.cos(((s.spinY + s.spinScroll) * Math.PI) / 180));
        shadowRef.current.style.transform =
          `translate3d(${(cx - CARD_W / 2 + 24).toFixed(1)}px, ${(cy - CARD_H / 2 + 32).toFixed(1)}px, 0)` +
          ` rotate(${((s.angle * 180) / Math.PI).toFixed(2)}deg)` +
          ` scaleX(${(0.12 + 0.88 * face).toFixed(3)})`;
        shadowRef.current.style.opacity = (0.22 + 0.4 * face).toFixed(3);
      }
      if (pathRef.current) {
        const d = ropePath(pts);
        pathRef.current.setAttribute('d', d);
      }
    };

    // Settle the intro and paint one frame synchronously. Without this the strap
    // has no `d` and the card has no transform until rAF first fires — and if
    // rAF is throttled (hidden tab, background window) the scene stays broken:
    // card parked at 0,0 with no lanyard.
    for (let i = 0; i < 2; i++) frame();

    const tick = () => {
      raf = requestAnimationFrame(tick);
      frame();
    };
    raf = requestAnimationFrame(tick);

    const onResize = () => {
      const oldX = s.anchorBase;
      setAnchor();
      const dx = s.anchorBase - oldX;
      s.pts.forEach(p => { p.x += dx; p.px += dx; });
    };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, []);

  /* ── drag ───────────────────────────────────────────────────────────────── */
  const onPointerDown = useCallback((e) => {
    const s = sim.current;
    askTilt();
    const [px, py] = toStage(e.clientX, e.clientY, sceneRef.current.getBoundingClientRect(), stage);
    const end = s.pts[s.pts.length - 1];
    // grab point relative to the attach point, in card-local coords
    const dx = px - end.x;
    const dy = py - end.y;
    const c = Math.cos(-s.angle), sn = Math.sin(-s.angle);
    s.grab = { lx: dx * c - dy * sn, ly: dx * sn + dy * c };
    s.dragging = true;
    s.moved = false;
    // sampled here, not on release — the drag spring bleeds velocity, so by
    // pointerup a card that was swinging looks still
    // spin is deliberately ignored: a card still finishing a flip is a perfectly
    // good thing to tap again, and gating on it made taps feel broken
    s.wasAtRest = isAtRest(s.pts, s.angleVel, 0);
    s.target = { x: px, y: py };
    s.heldVx = 0;
    setHint(false);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }, [stage, askTilt]);

  // Move only records where the cursor is. All force lives in the rAF loop, so
  // a burst of pointer events in one frame can't multiply the pull.
  const onPointerMove = useCallback((e) => {
    const s = sim.current;
    if (!s.dragging) return;
    const [px, py] = toStage(e.clientX, e.clientY, sceneRef.current.getBoundingClientRect(), stage);
    s.moved = true;
    s.target = { x: px, y: py };
  }, [stage]);

  const onPointerUp = useCallback(() => {
    const s = sim.current;
    if (!s.dragging) return;
    s.dragging = false;
    // Only cap the extremes here. Chopping every velocity by a fixed fraction
    // in one frame is itself a visible snap — the taper is done over time by
    // releaseDamp instead.
    for (let i = 1; i < s.pts.length; i++) {
      const p = s.pts[i];
      let vx2 = (p.x - p.px) * 0.88;
      let vy2 = (p.y - p.py) * 0.88;
      const sp = Math.hypot(vx2, vy2);
      if (sp > 9) { vx2 = (vx2 / sp) * 9; vy2 = (vy2 / sp) * 9; }
      p.px = p.x - vx2;
      p.py = p.y - vy2;
    }
    s.releaseDamp = 1;
    // No extra twist on release — the tilt is the strap's job.
    s.angleVel = clamp(s.angleVel * 0.4, -0.025, 0.025);
    const vx = s.heldVx;
    if (s.moved) {
      // only a real throw turns it; a slow drag just swings
      s.spinTarget += flipHalves(vx) * 180;
    } else if (s.wasAtRest) {
      // Tap = one clean half turn away from the edge pushed, measured from the
      // existing target rather than the live angle: mid-settle taps then queue
      // up instead of fighting the turn already under way.
      const dir = flipDirection(s.grab?.lx);
      s.spinTarget = Math.round(s.spinTarget / 180) * 180 + dir * 180;
    } else {
      s.angleVel = clamp(s.angleVel * 0.4, -0.02, 0.02); // tap mid-swing = catch it
    }
    s.target = null;
  }, []);

  /* ── timeline dial ──────────────────────────────────────────────────────── */
  // Scrub by writing the scroll position: one source of truth, so nothing can
  // disagree with anything else about where we are.
  const scrubTo = useCallback((p, snap) => {
    const lenis = lenisRef.current;
    if (!lenis) return;
    const limit = limitRef.current
      || lenis.limit
      || (document.documentElement.scrollHeight - window.innerHeight);
    if (limit <= 0) return;
    const target = (clamp(p, 0, STOPS.length - 1) / (STOPS.length - 1)) * limit;
    lenis.scrollTo(target, snap
      ? { duration: 0.55, force: true }
      : { immediate: true, force: true });
  }, []);

  const angleAt = (e) => {
    const r = dialRef.current.getBoundingClientRect();
    // window is DIAL_VIS wide and shows the wheel's left edge, so the centre is
    // (DIAL_BOX/2 - DIAL_VIS) px beyond the window's right edge
    const cx = r.right + (DIAL_BOX / 2 - DIAL_VIS);
    return pointerAngle(e.clientX - cx, e.clientY - (r.top + r.height / 2));
  };

  const onDialDown = useCallback((e) => {
    askTilt();
    armAudio();   // desktop clicks too, not just touch
    detentRef.current = detentIndex(progressRef.current);
    dragDial.current = { deg: angleAt(e), from: progressRef.current };
    setHint(false);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }, [askTilt, armAudio]);

  const onDialMove = useCallback((e) => {
    const g = dragDial.current;
    if (!g) return;
    const p = crownProgress(g.from, g.deg, angleAt(e), STOPS.length);
    const d = detentIndex(p);
    if (detentRef.current !== null && d !== detentRef.current) {
      detentRef.current = d;
      click();
    }
    scrubTo(p, false);
  }, [scrubTo, click]);

  const onDialUp = useCallback((e) => {
    const g = dragDial.current;
    if (!g) return;
    dragDial.current = null;
    detentRef.current = null;
    // continuous while turning, decisive on release
    scrubTo(Math.round(crownProgress(g.from, g.deg, angleAt(e), STOPS.length)), true);
  }, [scrubTo]);

  /* ── background: interpolate between stop palettes ──────────────────────── */
  const lo = STOPS[secLo];
  const hi = STOPS[secHi];
  const mixAt = (a, b, tt) => {
    const pa = a.match(/\w\w/g).map(h => parseInt(h, 16));
    const pb = b.match(/\w\w/g).map(h => parseInt(h, 16));
    return `rgb(${pa.map((v, i) => Math.round(lerp(v, pb[i], tt))).join(',')})`;
  };

  // Two different clocks on purpose. Ambient colour (strap, backdrop) blends
  // across the WHOLE section so it can never step; the card's content keeps the
  // late swap, because information should change at one definite moment.
  const ambient = smoother(frac);
  const t = swap;
  const mix = (a, b) => mixAt(a, b, ambient);
  const accentNow = mixAt(lo.accent.slice(1), hi.accent.slice(1), ambient);
  // light mode washes each stop's accent toward white instead of toward black
  const pair = (s) => (light ? [tintHex(s.accent, 0.95), tintHex(s.accent, 0.8)] : s.bg);
  const bg0 = mix(pair(lo)[0].slice(1), pair(hi)[0].slice(1));
  const floor = light ? '#eceef3' : '#05060a';
  const yearNow = Math.round(lerp(endYear(lo), hi.end === null ? endYear(hi) : hi.year, t));
  // repeated enough to run the length of the strap at any window height
  const strapText = `${stop.company.toUpperCase()}   ·   `.repeat(6);

  const iconBtn = {
    width: 34, height: 34, borderRadius: 999, display: 'grid', placeItems: 'center',
    cursor: 'pointer', color: fg(0.9),
    background: light ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.08)',
    border: `1px solid ${fg(light ? 0.16 : 0.18)}`,
    backdropFilter: 'blur(10px)',
    boxShadow: light ? '0 3px 10px rgba(0,0,0,0.1)' : '0 3px 10px rgba(0,0,0,0.35)',
  };

  return (
    <div style={{ background: floor, minHeight: '100vh' }}>
      <style>{`
        @keyframes lnd-fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @keyframes lnd-pulse {
          0%   { transform: scale(0.55); opacity: 0.85; }
          70%  { transform: scale(1.7);  opacity: 0; }
          100% { transform: scale(1.7);  opacity: 0; }
        }
        .lnd-card {
          cursor: grab;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
        .lnd-card:active { cursor: grabbing; }
        .lnd-link {
          width: 26px; height: 26px; border-radius: 50%;
          display: grid; place-items: center; cursor: pointer;
          color: rgba(255,255,255,0.82);
          background: rgba(8,8,14,0.42);
          border: 1px solid rgba(255,255,255,0.18);
          backdrop-filter: blur(8px);
          box-shadow: 0 4px 10px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.13);
          transition: background .18s, color .18s, transform .18s, border-color .18s, box-shadow .18s;
        }
        .lnd-link svg { filter: drop-shadow(0 1px 2px rgba(0,0,0,0.65)); }
        .lnd-link:hover {
          background: rgba(255,255,255,0.92); color: #0a0a12;
          border-color: rgba(255,255,255,0.92); transform: scale(1.12);
          box-shadow: 0 8px 18px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.5);
        }
        .lnd-link:hover svg { filter: none; }
        .lnd-link:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
        .lnd-dot {
          position: relative; display: inline-block; width: 9px; height: 9px;
          border-radius: 50%; background: #fff; box-shadow: 0 0 10px rgba(255,255,255,0.85);
        }
        .lnd-dot::after {
          content: ''; position: absolute; inset: -6px; border-radius: 50%;
          border: 1.5px solid rgba(255,255,255,0.6);
          animation: lnd-pulse 3.2s ease-out infinite;
        }
        html.lenis, html.lenis body { height: auto; }
        .lenis.lenis-smooth { scroll-behavior: auto !important; }
        .lenis.lenis-stopped { overflow: hidden; }
      `}</style>

      {/* fixed scene: backdrop + rope + card */}
      <div
        ref={sceneRef}
        style={{
          position: 'fixed', inset: 0, overflow: 'hidden',
          background: bg0,
        }}
      >
        {/* rewind year, huge and faint, behind everything */}
        <div style={{
          position: 'absolute', left: 0, right: 0, top: '38%',
          textAlign: 'center', fontFamily: '"Segoe UI", Inter, sans-serif',
          fontSize: 'min(34vw, 420px)', fontWeight: 800, letterSpacing: '-0.05em',
          color: fg(light ? 0.05 : 0.035), lineHeight: 0.8, userSelect: 'none',
          transform: 'translateY(-50%)',
        }} aria-hidden="true">{yearNow}</div>

        {/* Everything below is in physics pixels, scaled as one unit so the
            geometry stays internally consistent at any viewport size. */}
        <div style={{
          position: 'absolute', inset: 0,
          transform: `scale(${stage})`, transformOrigin: '50% 0',
        }}>
        {/* the card's cast shadow — card-shaped, offset down-right as if lit
            from the top left, and squeezed as the card turns edge-on */}
        <div ref={shadowRef} style={{
          position: 'absolute', left: 0, top: 0, width: CARD_W, height: CARD_H,
          borderRadius: CARD_R, background: 'rgba(0,0,0,0.62)',
          filter: 'blur(26px)', pointerEvents: 'none', willChange: 'transform, opacity',
        }} />

        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          <path id="lnd-rope" ref={pathRef} d="" fill="none" stroke={accentNow} strokeWidth="19" strokeLinecap="round" />
          {/* printed webbing: the employer name repeated along the strap, the way
              a real lanyard is woven. Follows the live rope path via textPath. */}
          <text
            fill="rgba(255,255,255,0.9)"
            fontSize="11"
            fontWeight="700"
            letterSpacing="1.6"
            fontFamily='"Segoe UI", Inter, sans-serif'
            dominantBaseline="middle"
          >
            <textPath href="#lnd-rope" startOffset="7%">
              {strapText}
            </textPath>
          </text>
        </svg>

        {/* the card */}
        <div
          ref={cardRef}
          className="lnd-card"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{
            position: 'absolute', left: 0, top: 0, width: CARD_W, height: CARD_H,
            transformStyle: 'preserve-3d', touchAction: 'none',
          }}
        >
          {/* Side walls, inset by the corner radius so they never poke past the
              rounded silhouette. Mid-grey, not near-black: this is the milled
              edge of a plastic card, and it catches light. */}
          {[
            // Flat fills, one shade per side: brightest on top, darkest
            // underneath. Discrete steps read as facets, a gradient reads as a
            // wash.
            { w: CARD_T, h: CARD_H - CARD_R * 2, t: `rotateY(90deg) translateZ(${CARD_W / 2}px)`, c: '#cfd3dc' },
            { w: CARD_T, h: CARD_H - CARD_R * 2, t: `rotateY(-90deg) translateZ(${CARD_W / 2}px)`, c: '#9aa0ad' },
            { w: CARD_W - CARD_R * 2, h: CARD_T, t: `rotateX(90deg) translateZ(${CARD_H / 2}px)`, c: '#e2e5ec' },
            { w: CARD_W - CARD_R * 2, h: CARD_T, t: `rotateX(-90deg) translateZ(${CARD_H / 2}px)`, c: '#7c8290' },
          ].map(({ w, h, t, c }, i) => (
            <div key={i} style={{
              position: 'absolute', width: w, height: h,
              left: (CARD_W - w) / 2, top: (CARD_H - h) / 2,
              transform: t,
              background: c,
            }} />
          ))}

          {/* Metal clip, a child of the card so it turns and tilts WITH it.
              Local (CARD_W/2, SLOT_INSET) is exactly where the rope tip lands,
              so the joint holds at any angle. Two copies, one per side. */}
          {[1, -1].map(side => (
            <div key={side} style={{
              position: 'absolute', left: CARD_W / 2 - 11, top: SLOT_INSET - 21,
              width: 22, height: 30, borderRadius: 6, pointerEvents: 'none',
              background: side === 1 ? '#d9dce4' : '#a8adb9',
              boxShadow: '0 2px 7px rgba(0,0,0,0.5)',
              backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
              transform: side === 1
                ? `translateZ(${CARD_T / 2 + 0.6}px)`
                : `rotateY(180deg) translateZ(${CARD_T / 2 + 0.6}px)`,
            }} />
          ))}

          <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: `translateZ(${CARD_T / 2}px)` }}>
            <CardFront stops={STOPS} weights={weights} textWeights={textWeights} hint={hint} />
          </div>
          <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: `rotateY(180deg) translateZ(${CARD_T / 2}px)` }}>
            <CardBack stops={STOPS} textWeights={textWeights} />
          </div>
        </div>
        </div>

        {/* Left copy. Hidden below 1024px: at narrow widths it has nowhere to go
            but under the card, where every line wraps to one word. The card face
            already carries company, role and dates, so nothing is lost. */}
        {!narrow && (
          <div style={{
            position: 'absolute', left: 'max(5vw, 32px)', top: '50%', transform: 'translateY(-50%)',
            width: 'min(30vw, 400px)', fontFamily: '"Segoe UI", Inter, sans-serif',
            color: fg(1), pointerEvents: 'none',
          }}>
            <div style={{ position: 'relative', height: 320 }}>
              {STOPS.map((s, i) => (
                <div key={s.id} style={{
                  position: 'absolute', inset: 0, opacity: textWeights[i],
                  transform: `translateY(${((ease - i) * 22).toFixed(1)}px)`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                    <span style={{ width: 26, height: 2, background: s.accent, flex: 'none' }} />
                    <span style={{ fontSize: 11.5, letterSpacing: '2.6px', color: s.accent, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {spanLabel(s)}
                    </span>
                  </div>

                  <div style={{ fontSize: 'clamp(28px, 3.2vw, 46px)', fontWeight: 700, letterSpacing: '-1.4px', lineHeight: 1.08 }}>
                    {s.company}
                  </div>

                  <div style={{
                    fontSize: 16.5, color: fg(0.66), marginTop: 12, lineHeight: 1.4,
                    display: 'flex', alignItems: 'center', gap: 7,
                  }}>
                    {s.role}{s.wink ? <Wink size={18} /> : null}
                  </div>

                  <div style={{ fontSize: 14.5, color: fg(0.68), marginTop: 20, lineHeight: 1.8, maxWidth: '34ch' }}>
                    {s.note}
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 22 }}>
                    {s.tech.map(tag => (
                      <span key={tag} style={{
                        fontSize: 11, letterSpacing: '0.4px', color: fg(0.72),
                        border: `1px solid ${fg(0.16)}`, borderRadius: 4, padding: '4px 9px',
                        whiteSpace: 'nowrap',
                      }}>{tag}</span>
                    ))}
                  </div>

                  <div style={{ fontSize: 12, color: fg(0.6), marginTop: 22, letterSpacing: '0.3px' }}>
                    {s.place}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Crown-style timeline. The wheel is large and mostly off the right
            edge; only DIAL_VIS px of it shows. The readout sits OUTSIDE the
            wheel, because anything drawn inside a clipped circle whose centre
            is past the viewport edge gets cut. Turning writes to the scroll
            position, so every other element follows as it always did. */}
        <div style={{
          // On narrow screens the card owns the middle of the screen, so the
          // crown drops into the gap between the card and the copy below it.
          position: 'absolute', right: 0, top: narrow ? '64%' : '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: narrow ? 'column' : 'row',
          alignItems: narrow ? 'flex-end' : 'center',
          gap: narrow ? 6 : 12,
          fontFamily: '"Segoe UI", Inter, sans-serif', pointerEvents: 'none',
        }}>
          <div style={{ textAlign: 'right', lineHeight: 1.15, paddingRight: narrow ? 6 : 0 }}>
            <div style={{ fontSize: narrow ? 13 : 18, fontWeight: 700, color: fg(0.95), letterSpacing: '-0.3px' }}>
              {stop.end === null ? 'now' : stop.year}
            </div>
            <div style={{ fontSize: 8.5, letterSpacing: '1.6px', color: fg(0.5), marginTop: 3 }}>
              TURN
            </div>
          </div>

          <div
            ref={dialRef}
            onPointerDown={onDialDown}
            onPointerMove={onDialMove}
            onPointerUp={onDialUp}
            onPointerCancel={onDialUp}
            style={{
              position: 'relative', width: DIAL_VIS, height: DIAL_BOX,
              overflow: 'hidden', pointerEvents: 'auto',
              touchAction: 'none', cursor: 'grab',
            }}
          >
            <svg
              width={DIAL_BOX} height={DIAL_BOX}
              viewBox={`0 0 ${DIAL_BOX} ${DIAL_BOX}`}
              style={{ position: 'absolute', top: 0, right: -(DIAL_BOX - DIAL_VIS) }}
            >
              {(() => {
                const c = DIAL_BOX / 2;
                const p = clamp(ease, 0, STOPS.length - 1);
                // The SVG is pulled right so only its leftmost DIAL_VIS px show, and
                // the wheel centre sits outside that strip. Visible means a SMALL x,
                // not a large one: the earlier test kept the hidden half instead.
                const shows = (deg) => dialPoint(c, c, DIAL_R, deg)[0] <= DIAL_VIS + 6;

                const [ax, ay] = dialPoint(c, c, DIAL_R + 4, DIAL_CENTER - 62);
                const [bx, by] = dialPoint(c, c, DIAL_R + 4, DIAL_CENTER + 62);

                const minors = [];
                const teeth = (STOPS.length - 1) * (DIAL_SUBS + 1);
                for (let m = 0; m <= teeth; m++) {
                  if (m % (DIAL_SUBS + 1) === 0) continue;
                  const deg = crownAngle(m / (DIAL_SUBS + 1), p);
                  if (!shows(deg)) continue;
                  const [x1, y1] = dialPoint(c, c, DIAL_R - 7, deg);
                  const [x2, y2] = dialPoint(c, c, DIAL_R + 3, deg);
                  minors.push(<line key={'m' + m} x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={fg(0.16)} strokeWidth="1.2" strokeLinecap="round" />);
                }

                return (
                  <>
                    <path d={`M ${ax} ${ay} A ${DIAL_R + 4} ${DIAL_R + 4} 0 0 1 ${bx} ${by}`}
                      fill="none" stroke={fg(0.12)} strokeWidth="1" />
                    {minors}

                    {STOPS.map((st, i) => {
                      const deg = crownAngle(i, p);
                      if (!shows(deg)) return null;
                      const off = Math.abs(deg - DIAL_CENTER) / (DIAL_STEP * 2.2);
                      const on = Math.abs(i - p) < 0.5;
                      const [x1, y1] = dialPoint(c, c, DIAL_R - (on ? 26 : 16), deg);
                      const [x2, y2] = dialPoint(c, c, DIAL_R + 3, deg);
                      return (
                        <line key={st.id} x1={x1} y1={y1} x2={x2} y2={y2}
                          stroke={on ? st.accent : fg(clamp(0.6 - off * 0.38, 0.12, 0.6))}
                          strokeWidth={on ? 3 : 1.7} strokeLinecap="round" />
                      );
                    })}

                    {/* fixed marker the wheel is read against */}
                    {(() => {
                      const [x1, y1] = dialPoint(c, c, DIAL_R - 40, DIAL_CENTER);
                      const [x2, y2] = dialPoint(c, c, DIAL_R - 32, DIAL_CENTER);
                      return <line x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={accentNow} strokeWidth="3" strokeLinecap="round" />;
                    })()}
                  </>
                );
              })()}
            </svg>
          </div>
        </div>

        {/* Narrow layout: the side column has nowhere to go, so the same copy
            runs below the card. Same textWeights, so it swaps on the same beat
            as everything else. */}
        {narrow && (
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 58, padding: '0 22px',
            textAlign: 'center', fontFamily: '"Segoe UI", Inter, sans-serif',
            color: fg(1), pointerEvents: 'none',
          }}>
            <div style={{ position: 'relative', height: 196 }}>
              {STOPS.map((s, i) => (
                <div key={s.id} style={{ position: 'absolute', inset: 0, opacity: textWeights[i] }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 9, marginBottom: 12,
                  }}>
                    <span style={{ width: 18, height: 2, background: s.accent, flex: 'none' }} />
                    <span style={{
                      fontSize: 10.5, letterSpacing: '2.2px', color: s.accent,
                      textTransform: 'uppercase', whiteSpace: 'nowrap',
                    }}>{spanLabel(s)}</span>
                    <span style={{ width: 18, height: 2, background: s.accent, flex: 'none' }} />
                  </div>

                  <div style={{ fontSize: 27, fontWeight: 700, letterSpacing: '-0.9px', lineHeight: 1.12 }}>
                    {s.company}
                  </div>

                  <div style={{
                    fontSize: 14, color: fg(0.66), marginTop: 7, lineHeight: 1.35,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}>
                    {s.role}{s.wink ? <Wink size={15} /> : null}
                  </div>

                  <div style={{
                    fontSize: 12.5, color: fg(0.68), marginTop: 12, lineHeight: 1.65,
                    maxWidth: '38ch', marginLeft: 'auto', marginRight: 'auto',
                  }}>
                    {s.note}
                  </div>

                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14,
                    justifyContent: 'center',
                  }}>
                    {s.tech.map(tag => (
                      <span key={tag} style={{
                        fontSize: 10, letterSpacing: '0.3px', color: fg(0.72),
                        border: `1px solid ${fg(0.16)}`, borderRadius: 4, padding: '3px 7px',
                        whiteSpace: 'nowrap',
                      }}>{tag}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* header */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, padding: '22px max(4vw, 20px)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontFamily: '"Segoe UI", Inter, sans-serif',
        }}>
          <div style={{ color: fg(1), fontWeight: 600, letterSpacing: '-0.3px' }}>Yoyal Limbu</div>
          <div style={{ display: 'flex', gap: 9 }}>
            {/* a real link, not a route: /blog/ is a static page, so it must do
                a full navigation rather than be swallowed by the SPA */}
            <a href="/blog/" style={{ ...iconBtn, textDecoration: 'none' }}
              aria-label="Writing" title="Writing">
              <FiFileText size={15} />
            </a>
            <button
              style={iconBtn}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
              title={theme === 'dark' ? 'Light' : 'Dark'}
            >
              {theme === 'dark' ? <FiSun size={15} /> : <FiMoon size={15} />}
            </button>
            <button
              style={iconBtn}
              onClick={onEnterDesktop}
              aria-label={narrow ? 'Open apps' : 'Desktop mode'}
              title={narrow ? 'Open apps' : 'Desktop mode'}
            >
              {/* a phone gets an app launcher, a real screen gets a desktop */}
              {narrow ? <FiGrid size={15} /> : <FiMonitor size={15} />}
            </button>
          </div>
        </div>

      </div>

      {/* scroll track — one viewport per stop, drives `progress` */}
      <div style={{ position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
        {STOPS.map(s => <section key={s.id} style={{ height: '100vh' }} aria-label={s.company} />)}
      </div>
    </div>
  );
}
